import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Target,
  BarChart3,
  PieChart,
} from "lucide-react";
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

export function QualityMetricsDashboard() {
  const { data: inspections } = useQuery({
    queryKey: ["quality-inspections-metrics"],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select("*")
        .order("inspection_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: defects } = useQuery({
    queryKey: ["quality-defects-metrics"],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_defects")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Calculate metrics
  const today = new Date();
  const last30Days = subDays(today, 30);
  const last7Days = subDays(today, 7);

  const recentInspections = inspections?.filter(
    (i) => new Date(i.inspection_date) >= last30Days
  ) || [];

  const weeklyInspections = inspections?.filter(
    (i) => new Date(i.inspection_date) >= last7Days
  ) || [];

  const passedInspections = recentInspections.filter((i) => i.status === "passed");
  const failedInspections = recentInspections.filter((i) => i.status === "failed");

  const passRate = recentInspections.length > 0
    ? Math.round((passedInspections.length / (passedInspections.length + failedInspections.length)) * 100) || 0
    : 0;

  const previousMonthInspections = inspections?.filter((i) => {
    const date = new Date(i.inspection_date);
    return date >= subDays(last30Days, 30) && date < last30Days;
  }) || [];

  const previousPassRate = previousMonthInspections.length > 0
    ? Math.round(
        (previousMonthInspections.filter((i) => i.status === "passed").length /
          previousMonthInspections.filter((i) => i.status === "passed" || i.status === "failed").length) *
          100
      ) || 0
    : 0;

  const passRateTrend = passRate - previousPassRate;

  // Defects by severity
  const recentDefects = defects?.filter((d) => new Date(d.created_at) >= last30Days) || [];
  const defectsBySeverity = {
    critical: recentDefects.filter((d) => d.severity === "critical").length,
    major: recentDefects.filter((d) => d.severity === "major").length,
    minor: recentDefects.filter((d) => d.severity === "minor").length,
    cosmetic: recentDefects.filter((d) => d.severity === "cosmetic").length,
  };

  // Defects by type (top 5)
  const defectsByType = recentDefects.reduce((acc: Record<string, number>, d) => {
    acc[d.defect_type] = (acc[d.defect_type] || 0) + 1;
    return acc;
  }, {});

  const topDefectTypes = Object.entries(defectsByType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Inspections by type
  const inspectionsByType = {
    incoming: recentInspections.filter((i) => i.inspection_type === "incoming").length,
    in_process: recentInspections.filter((i) => i.inspection_type === "in_process").length,
    final: recentInspections.filter((i) => i.inspection_type === "final").length,
    random: recentInspections.filter((i) => i.inspection_type === "random").length,
  };

  // Weekly trend
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
  const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const weeklyData = daysOfWeek.map((day) => {
    const dayInspections = inspections?.filter((i) => {
      const inspDate = new Date(i.inspection_date);
      return (
        inspDate.getDate() === day.getDate() &&
        inspDate.getMonth() === day.getMonth() &&
        inspDate.getFullYear() === day.getFullYear()
      );
    }) || [];

    return {
      day: format(day, "EEE", { locale: es }),
      total: dayInspections.length,
      passed: dayInspections.filter((i) => i.status === "passed").length,
      failed: dayInspections.filter((i) => i.status === "failed").length,
    };
  });

  const maxWeeklyValue = Math.max(...weeklyData.map((d) => d.total), 1);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasa de Aprobacion</p>
                <p className="text-3xl font-bold">{passRate}%</p>
                <div className="flex items-center gap-1 mt-1">
                  {passRateTrend >= 0 ? (
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  )}
                  <span
                    className={cn(
                      "text-sm",
                      passRateTrend >= 0 ? "text-green-500" : "text-red-500"
                    )}
                  >
                    {passRateTrend >= 0 ? "+" : ""}{passRateTrend}% vs mes anterior
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-full bg-green-500/10">
                <Target className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inspecciones (30 dias)</p>
                <p className="text-3xl font-bold">{recentInspections.length}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {weeklyInspections.length} esta semana
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-500/10">
                <BarChart3 className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Defectos Totales</p>
                <p className="text-3xl font-bold">{recentDefects.length}</p>
                <p className="text-sm text-red-500 mt-1">
                  {defectsBySeverity.critical} criticos
                </p>
              </div>
              <div className="p-3 rounded-full bg-yellow-500/10">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Defectos por Inspeccion</p>
                <p className="text-3xl font-bold">
                  {recentInspections.length > 0
                    ? (recentDefects.length / recentInspections.length).toFixed(1)
                    : "0"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Promedio</p>
              </div>
              <div className="p-3 rounded-full bg-purple-500/10">
                <PieChart className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Weekly Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Inspecciones esta Semana</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {weeklyData.map((day, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="capitalize w-12">{day.day}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {day.passed}
                      </span>
                      <span className="text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        {day.failed}
                      </span>
                      <span className="w-8 text-right font-medium">{day.total}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 h-2">
                    {day.passed > 0 && (
                      <div
                        className="bg-green-500 rounded-full transition-all"
                        style={{ width: `${(day.passed / maxWeeklyValue) * 100}%` }}
                      />
                    )}
                    {day.failed > 0 && (
                      <div
                        className="bg-red-500 rounded-full transition-all"
                        style={{ width: `${(day.failed / maxWeeklyValue) * 100}%` }}
                      />
                    )}
                    {day.total === 0 && (
                      <div className="bg-gray-200 rounded-full w-full" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Defects by Severity */}
        <Card>
          <CardHeader>
            <CardTitle>Defectos por Severidad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    Criticos
                  </span>
                  <span className="font-medium">{defectsBySeverity.critical}</span>
                </div>
                <Progress
                  value={(defectsBySeverity.critical / (recentDefects.length || 1)) * 100}
                  className="h-2 [&>div]:bg-red-500"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    Mayores
                  </span>
                  <span className="font-medium">{defectsBySeverity.major}</span>
                </div>
                <Progress
                  value={(defectsBySeverity.major / (recentDefects.length || 1)) * 100}
                  className="h-2 [&>div]:bg-orange-500"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    Menores
                  </span>
                  <span className="font-medium">{defectsBySeverity.minor}</span>
                </div>
                <Progress
                  value={(defectsBySeverity.minor / (recentDefects.length || 1)) * 100}
                  className="h-2 [&>div]:bg-yellow-500"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    Cosmeticos
                  </span>
                  <span className="font-medium">{defectsBySeverity.cosmetic}</span>
                </div>
                <Progress
                  value={(defectsBySeverity.cosmetic / (recentDefects.length || 1)) * 100}
                  className="h-2 [&>div]:bg-blue-500"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspections by Type */}
        <Card>
          <CardHeader>
            <CardTitle>Inspecciones por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-3xl font-bold text-blue-600">{inspectionsByType.incoming}</p>
                <p className="text-sm text-muted-foreground">Recepcion</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-3xl font-bold text-purple-600">{inspectionsByType.in_process}</p>
                <p className="text-sm text-muted-foreground">En Proceso</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-3xl font-bold text-green-600">{inspectionsByType.final}</p>
                <p className="text-sm text-muted-foreground">Final</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-3xl font-bold text-orange-600">{inspectionsByType.random}</p>
                <p className="text-sm text-muted-foreground">Aleatoria</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Defect Types */}
        <Card>
          <CardHeader>
            <CardTitle>Tipos de Defectos Frecuentes</CardTitle>
          </CardHeader>
          <CardContent>
            {topDefectTypes.length > 0 ? (
              <div className="space-y-3">
                {topDefectTypes.map(([type, count], index) => (
                  <div key={type} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{type}</p>
                      <Progress
                        value={(count / (topDefectTypes[0]?.[1] || 1)) * 100}
                        className="h-1.5 mt-1"
                      />
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No hay defectos registrados</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
